/**
 * ToastProvider — 전역 토스트 알림 (PC·fold 상단 중앙 / MO 상단 전체 폭, 자동 dismiss).
 *
 * 사용:
 *   const toast = useToast();
 *   toast.success("적용 완료");
 *   toast.error("저장 실패: ...");
 *   toast.info("...");
 *
 * 토스트 자체 클릭 시 즉시 dismiss. 마우스를 올리면 타이머·남은 시간 막대가 멈춤.
 * 디폴트 노출 — 성공 3.5초 · 안내 4초 · 오류 6.5초 (durationMs 로 개별 지정 가능).
 * 우상단에서 놓친다는 피드백으로 위치·색·시간 변경(2026-10-13, 샘플 tmp/toast-preview/).
 */
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { ToastContext, type ToastKind } from "./toast-context";

interface ToastItem {
  id: number;
  kind: ToastKind;
  text: string;
  durationMs: number;
  leaving: boolean;
}

const DEFAULT_MS: Record<ToastKind, number> = { ok: 3500, info: 4000, error: 6500 };
const LEAVE_MS = 180; // .toast.is-leaving 애니메이션 길이와 맞춤

let _idSeq = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, LEAVE_MS);
  }, []);

  const push = useCallback((kind: ToastKind, text: string, durationMs?: number) => {
    const id = _idSeq++;
    setItems((prev) => [
      ...prev,
      { id, kind, text, durationMs: durationMs ?? DEFAULT_MS[kind], leaving: false },
    ]);
  }, []);

  return (
    <ToastContext.Provider value={{ push }}>
      {children}
      <div className="toast-container" role="status" aria-live="polite">
        {items.map((t) => (
          <ToastView key={t.id} item={t} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: (id: number) => void }) {
  const remainingRef = useRef(item.durationMs);
  const startedRef = useRef(0);
  const timerRef = useRef<number | undefined>(undefined);

  const start = useCallback(() => {
    startedRef.current = Date.now();
    timerRef.current = window.setTimeout(() => onDismiss(item.id), remainingRef.current);
  }, [item.id, onDismiss]);

  const pause = () => {
    window.clearTimeout(timerRef.current);
    remainingRef.current -= Date.now() - startedRef.current;
  };

  useEffect(() => {
    start();
    return () => window.clearTimeout(timerRef.current);
  }, [start]);

  return (
    <button
      type="button"
      className={`toast toast-${item.kind}${item.leaving ? " is-leaving" : ""}`}
      style={{ "--toast-dur": `${item.durationMs}ms` } as CSSProperties}
      onClick={() => onDismiss(item.id)}
      onMouseEnter={pause}
      onMouseLeave={start}
      title="클릭하여 닫기"
    >
      <span className="toast-icon" aria-hidden>
        {item.kind === "ok" ? "✓" : item.kind === "error" ? "!" : "i"}
      </span>
      <span className="toast-text">{item.text}</span>
      <span className="toast-close" aria-hidden>×</span>
      <span className="toast-bar" aria-hidden />
    </button>
  );
}
